import type {
  VettingCheckResolution,
  VettingCheckResult,
  VettingCheckStatus,
} from '../types/vetting.types';

/**
 * Single status -> label -> color mapping, reused everywhere (Pending tab,
 * provider table, provider editor) so wording never drifts between surfaces.
 * Consider is amber, not red — it's a decision-required state, not a failure
 * (per new_requirement/099-checkr-implementation-guide.md §7.2).
 */
export const VETTING_STATUS_COPY: Record<VettingCheckStatus, { label: string; className: string }> = {
  NOT_STARTED: { label: 'Not started', className: 'bg-gray-100 text-gray-800' },
  INVITATION_SENT: { label: 'Waiting on SP', className: 'bg-blue-100 text-blue-800' },
  IN_PROGRESS: { label: 'In progress', className: 'bg-blue-100 text-blue-800' },
  COMPLETE: { label: 'Clear', className: 'bg-green-100 text-green-800' },
  EXPIRED: { label: 'Expired', className: 'bg-red-100 text-red-800' },
  CANCELED: { label: 'Canceled', className: 'bg-red-100 text-red-800' },
  ERROR: { label: 'Retry needed', className: 'bg-red-100 text-red-800' },
};

export const CONSIDER_COPY = { label: 'Consider — review required', className: 'bg-amber-100 text-amber-800' };

export const OVERRIDDEN_COPY = { label: 'Consider — overridden', className: 'bg-green-100 text-green-800' };
export const DECLINED_COPY = { label: 'Declined', className: 'bg-red-100 text-red-800' };

/**
 * An admin decision (resolution) wins over the raw vendor result: an overridden
 * Consider keeps result=CONSIDER for audit, but is no longer "review required".
 */
export function vettingStatusCopy(
  status: VettingCheckStatus,
  result: VettingCheckResult,
  resolution?: VettingCheckResolution,
) {
  if (resolution === 'ADMIN_OVERRIDE') return OVERRIDDEN_COPY;
  if (resolution === 'DECLINED') return DECLINED_COPY;
  if (status === 'COMPLETE' && result === 'CONSIDER') return CONSIDER_COPY;
  return VETTING_STATUS_COPY[status];
}
